import { SettingsMenuLayout } from '../../../Layouts/SettingsMenuLayout';
import BaseUISystem from '../BaseUISystem';
import { GameScene } from '../../../State/Game/GameScene';
import { PopupID } from '../../../State/UI/PopupID';
import BaseMenu from 'System/UI/Menu/BaseMenu';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import { GameObjectEvent } from 'GameObject/GameObject';
import Game from 'Game';
import { UIActions } from 'State/UI/UIActions';
import { PlayerActions } from 'State/Player/PlayerActions';
import { AudioStreamType } from 'System/Audio/AudioSystem';
import IPlayerSettingsData from 'Player/PlayerProperty/Settings/IPlayerSettingsData';
import TextComponent from 'Component/Render/Text';
import SpriteComponent from "Component/Render/Sprite";
import Renderable from "Component/Renderable";
import { getLayout, GLayout } from 'Utility/layout/LayoutHelper';
import { getAudioMusic, getAudioSFX } from 'System/Audio/AudioHelper';
import SettingVolumeSeekBar, { createSettingVolumeSeekBar } from '../SettingVolumeSeekBar';
import { MUSIC_VOLUME_VALUE, SOUND_VOLUME_VALUE } from 'Constants/World';

interface ISettingsMenuShowParams {
    gameScene: GameScene;
    levelNumber?: number;
}

export class SettingsPopup extends BaseMenu {
    private layout: GLayout<SettingsMenuLayout>;
    private soundVolumeSlider: SettingVolumeSeekBar;
    private musicVolumeSlider: SettingVolumeSeekBar;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.SETTINGS_MENU, uiSystem, false);
    }

    public onShow(params: ISettingsMenuShowParams): void {
        super.onShow(params);
        this.layout = getLayout(SettingsMenuLayout);
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        const { close_btn, return_btn, exit_level_btn, music_btn, sound_btn, version_txt, contact_txt, policy_txt, terms_txt } = this.layout;
        const { musicSliderBackground, musicSliderFill, musicSliderHandle, soundSliderBackground, soundSliderFill, soundSliderHandle } = this.layout;
        [close_btn, return_btn, exit_level_btn, music_btn, sound_btn].forEach(o => applyDefaultButtonAnimation(o));
        close_btn.once(GameObjectEvent.SELECTED, () => UIActions.hidePopup(this.name));
        return_btn.once(GameObjectEvent.SELECTED, () => UIActions.hidePopup(this.name));
        exit_level_btn.once(GameObjectEvent.SELECTED, () => UIActions.showPopup(PopupID.CONFIRM_QUIT_LEVEL, {
            levelNumber: params.levelNumber,
            endLevelClicked: false,
        }));
        music_btn.on(GameObjectEvent.SELECTED, () => this.toggleAudio(AudioStreamType.MUSIC));
        sound_btn.on(GameObjectEvent.SELECTED, () => this.toggleAudio(AudioStreamType.SFX));

        //@ts-ignore
        version_txt.getComponent(TextComponent).text = "v" + FBInstant.getConfig().gameBuild;

        // Handle Return/Quit level button
        if (Game.store.getState().activeScene.result === GameScene.WORLD) {
            return_btn.getComponent(Renderable).container.visible = true;
            exit_level_btn.getComponent(Renderable).container.visible = false;
        } else {
            return_btn.getComponent(Renderable).container.visible = false;
            exit_level_btn.getComponent(Renderable).container.visible = true;
        }

        this.soundVolumeSlider = createSettingVolumeSeekBar(getAudioSFX(), soundSliderBackground, soundSliderHandle, soundSliderFill, (value) => {
            PlayerActions.setVolume({ type: AudioStreamType.SFX, value });
            this.setAudio();
        });

        this.musicVolumeSlider = createSettingVolumeSeekBar(getAudioMusic(), musicSliderBackground, musicSliderHandle, musicSliderFill, (value) => {
            PlayerActions.setVolume({ type: AudioStreamType.MUSIC, value });
            this.setAudio();
        });

        this.setAudio();
    }

    private setAudio(): void {
        const musicValue: number = getAudioMusic();
        const sfxValue: number = getAudioSFX();

        this.layout.music_txt.getComponent(TextComponent).text = Game.locale.getText(musicValue > 0 ? 'On' : 'Off');
        this.layout.sound_txt.getComponent(TextComponent).text = Game.locale.getText(sfxValue > 0 ? 'On' : 'Off');
        this.layout.music_btn.getComponent(SpriteComponent).texture = `audiomusic_${musicValue > 0 ? 1 : 0}`;
        this.layout.sound_btn.getComponent(SpriteComponent).texture = `audiosfx_${sfxValue > 0 ? 1 : 0}`;

        if (this.soundVolumeSlider && this.musicVolumeSlider) {
            this.soundVolumeSlider.setValue(sfxValue);
            this.musicVolumeSlider.setValue(musicValue);
        }
    }

    private toggleAudio = (type: AudioStreamType): void => {
        const volume = Game.store.getState().playerState.settings[type as keyof IPlayerSettingsData];
        let value = 0;
        if (typeof volume !== 'number') {
            value = volume ? 0 : type === AudioStreamType.MUSIC ? MUSIC_VOLUME_VALUE : SOUND_VOLUME_VALUE;
        } else {
            value = volume > 0 ? 0 : type === AudioStreamType.MUSIC ? MUSIC_VOLUME_VALUE : SOUND_VOLUME_VALUE;
        }
        PlayerActions.setVolume({ type, value });

        this.setAudio();
    };
}
